import { faPlay } from "@fortawesome/free-solid-svg-icons/faPlay";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";
import { ConversationMessage } from "../../../../../model/integration/dto/meta/MetaConversationMessage";
import { Rounded } from "../../../../../theme/Rounded";
import { Spacing } from "../../../../../theme/Spacing";
import { getMediaUrl } from "../../../../../utils/Media";
import { WAMediaRetentionInfo } from "./WAMediaRetentionInfo";
import { WAMediaUnavailable } from "./WAMediaUnavailable";
import { formatMediaDuration } from "./media-duration";
import { isMediaPending } from "./media-state";

const Media = styled.div`
  position: relative;
  width: fit-content;
  max-width: 100%;
`;

const Frame = styled.div`
  width: 17rem;
  max-width: 100%;
  border-radius: ${Rounded.md};
  overflow: hidden;
  line-height: 0;

  video {
    width: 100%;
    max-height: 20rem;
    display: block;
    background: #000;
  }
`;

const Poster = styled(Flex)`
  position: relative;
  width: 17rem;
  max-width: 100%;
  aspect-ratio: 4 / 3;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${Spacing.s};
  border-radius: ${Rounded.md};
  cursor: pointer;
  background: rgba(0, 0, 0, 0.55);
  color: ${({ theme }) => theme.white};
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.68);
  }
`;

const PlayBadge = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: ${Rounded.circle};
  background: rgba(255, 255, 255, 0.9);
  color: ${({ theme }) => theme.main};
`;

const Length = styled(Text)`
  position: absolute;
  inset-block-end: ${Spacing.s};
  inset-inline-start: ${Spacing.s};
  padding: 0 ${Spacing.xs};
  border-radius: ${Rounded.sm};
  background: rgba(0, 0, 0, 0.6);
  line-height: 1.5;
`;

const Caption = styled(Text)`
  margin-top: ${Spacing.xs};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  unicode-bidi: plaintext;
  text-align: start;
`;

interface Props {
  message: ConversationMessage;
}

export const WAVideoMessage: React.FC<Props> = ({ message }) => {
  const { mediaId, video, localPreviewUrl } = message;
  const { t } = useTranslation();
  const [src, setSrc] = useState("");

  // Same as the audio bubble: the preview is released once our stored copy
  // exists, and a src left pointing at it plays nothing
  useEffect(() => {
    if (!localPreviewUrl && src.startsWith("blob:")) {
      setSrc(mediaId ? getMediaUrl(mediaId) : "");
    }
  }, [localPreviewUrl, mediaId, src]);

  if (!mediaId && !localPreviewUrl) {
    return (
      <WAMediaUnavailable
        label={t("common.whatsapp.video")}
        expired={Boolean(message.mediaExpiredAt)}
        pending={isMediaPending(message)}
      />
    );
  }

  return (
    <div>
      <Media>
        {src ? (
          <Frame>
            <video controls preload="metadata" src={src} />
          </Frame>
        ) : (
          <Poster
            onClick={() => setSrc(localPreviewUrl || getMediaUrl(mediaId!))}
          >
            <PlayBadge>
              <AtiraIcon icon={faPlay} size="lg" />
            </PlayBadge>

            {message.mediaDuration ? (
              <Length fontSize="xs" color="white">
                {formatMediaDuration(Math.ceil(message.mediaDuration))}
              </Length>
            ) : null}
          </Poster>
        )}

        {mediaId ? (
          <WAMediaRetentionInfo createdAt={message.createdAt} />
        ) : null}
      </Media>

      {video?.caption ? <Caption fontSize="s">{video.caption}</Caption> : null}
    </div>
  );
};
